/* DailyRecommendationCard — proactive pre-dinner AI recommendation.
 *
 * Shown near the top of the Dashboard meals section. Loads the scheduler-
 * generated daily recommendation (no credit charge), displays it inline with
 * dismiss + opt-out actions.
 */
import React, { useState, useEffect, useCallback } from "react";
import AIModuleNotice from "@/components/AIModuleNotice";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Lightbulb, Sparkles, X, BellOff, Bell, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { usePushNotifications } from "@/hooks/usePushNotifications";

export default function DailyRecommendationCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const push = usePushNotifications(API);

  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [hidden, setHidden] = useState(() => {
    // iter327Y — persistent dismissal: ако потребителят е натиснал X, скриваме
    // картата завинаги (вместо само за днес). Може да се възстанови само от
    // настройки → enable daily recommendations.
    try { return localStorage.getItem("nutramilo_daily_rec_hidden") === "1"; } catch { return false; }
  });
  const [refreshing, setRefreshing] = useState(false);

  // Resolve current local hour + canonical meal slot in user's timezone. Used
  // both as API hint (so server picks the correct cached slot) and as display
  // title ("Препоръки за закуска/обяд/вечеря") — fixes the "10 AM showing
  // dinner" bug caused by UTC hour misalignment.
  const localHour = React.useMemo(() => new Date().getHours(), []);
  const localMealType = React.useMemo(() => {
    if (localHour <= 10) return "breakfast";
    if (localHour <= 14) return "lunch";
    if (localHour <= 17) return "snack";
    return "dinner";
  }, [localHour]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/dashboard/daily-recommendation`, {
        params: { local_hour: localHour },
      });
      const r = data?.recommendation;
      if (r && r.dismissed_at) {
        setHidden(true);
      } else {
        setRec(r || null);
      }
    } catch {
      setRec(null);
    } finally {
      setLoading(false);
    }
  }, [API, localHour]);

  useEffect(() => {
    load();
    // Refresh silently when the user logs a meal — the card shows whether their
    // logging matches or contradicts the cached AI guidance.
    const onChange = () => load();
    // iter327Z — re-show card when user restores hidden cards from Settings
    const onRestore = () => {
      try { setHidden(localStorage.getItem("nutramilo_daily_rec_hidden") === "1"); }
      catch { setHidden(false); }
    };
    window.addEventListener("meal-added", onChange);
    window.addEventListener("meal-log-changed", onChange);
    window.addEventListener("lab-uploaded", onChange);
    window.addEventListener("nutramilo:cards-restored", onRestore);
    return () => {
      window.removeEventListener("meal-added", onChange);
      window.removeEventListener("meal-log-changed", onChange);
      window.removeEventListener("lab-uploaded", onChange);
      window.removeEventListener("nutramilo:cards-restored", onRestore);
    };
  }, [load]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { data } = await axios.post(`${API}/dashboard/daily-recommendation/refresh`, {
        local_hour: localHour,
      });
      const r = data?.recommendation;
      if (r) {
        setRec(r);
        setHidden(false);
        toast.success(bg ? "Препоръката е обновена въз основа на днешния ви лог" : "Recommendation refreshed with today's log");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(bg ? `Неуспех: ${detail}` : `Failed: ${detail}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDismiss = async () => {
    setHidden(true);
    // iter327Y — permanent client-side hide (потребителят натисна X). Backend
    // dismiss остава "за днес" — UI скрива завинаги докато не се reset-не от настройки.
    try { localStorage.setItem("nutramilo_daily_rec_hidden", "1"); } catch {}
    try {
      await axios.post(`${API}/dashboard/daily-recommendation/dismiss`);
    } catch { /* silent */ }
  };

  const handleOptOut = async () => {
    if (!window.confirm(bg ? "Изключете ежедневните препоръки?" : "Turn off daily recommendations?")) return;
    try {
      await axios.post(`${API}/dashboard/daily-recommendation/prefs`, { enabled: false });
      toast.success(bg ? "Изключено. Може да включите обратно от настройки." : "Disabled. Re-enable from settings.");
      setHidden(true);
    } catch {
      toast.error(bg ? "Грешка" : "Error");
    }
  };

  if (loading || hidden || !rec?.recommendations) return null;

  const generatedTime = rec.generated_at ? new Date(rec.generated_at).toLocaleTimeString(bg ? "bg-BG" : "en-US", { hour: "2-digit", minute: "2-digit" }) : "";
  const ctx = rec.context_used || {};

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border-2 p-4 sm:p-5 ${
        isDark
          ? "bg-gradient-to-br from-emerald-950/40 via-sky-950/30 to-indigo-950/40 border-emerald-800/50"
          : "bg-gradient-to-br from-emerald-50 via-sky-50 to-indigo-50 border-emerald-200"
      }`}
      data-testid="daily-recommendation-card"
    >
      {/* Decorative sparkle */}
      <div className="absolute -top-2 -right-2 opacity-20">
        <Sparkles className="w-24 h-24 text-emerald-500" />
      </div>

      <div className="relative">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-sky-500 flex items-center justify-center shadow-md shrink-0">
              <Lightbulb className="w-4 h-4 text-white" />
            </div>
            <div>
              <div className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
                {bg ? "Вашата ежедневна препоръка" : "Your daily recommendation"}
                {generatedTime && <span className={`ml-1.5 font-normal ${isDark ? "text-gray-500" : "text-gray-400"}`}>· {generatedTime}</span>}
              </div>
              <div className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`} data-testid="daily-rec-title">
                {(() => {
                  // Use the rec's own meal_type if available, otherwise infer from user's local hour.
                  const slot = rec?.meal_type || localMealType;
                  const titles = bg
                    ? { breakfast: "Препоръки за закуската", lunch: "Препоръки за обяда", snack: "Препоръки за следобедна закуска", dinner: "Препоръки за вечерята" }
                    : { breakfast: "Breakfast recommendations", lunch: "Lunch recommendations", snack: "Snack recommendations", dinner: "Dinner recommendations" };
                  return titles[slot] || titles.dinner;
                })()}
                {rec?.source === "user-refresh" && (
                  <span className={`ml-1.5 text-[9px] font-normal uppercase tracking-wider ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                    {bg ? "· актуализирано" : "· refreshed"}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-white/5 text-gray-400" : "hover:bg-black/5 text-gray-500"} ${refreshing ? "opacity-50" : ""}`}
              data-testid="daily-rec-refresh"
              title={bg ? "Обнови според днешния лог" : "Refresh with today's log"}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => setExpanded(!expanded)}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-white/5 text-gray-400" : "hover:bg-black/5 text-gray-500"}`}
              data-testid="daily-rec-toggle"
              title={expanded ? (bg ? "Свий" : "Collapse") : (bg ? "Разгърни" : "Expand")}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button
              onClick={handleDismiss}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-white/5 text-gray-400" : "hover:bg-black/5 text-gray-500"}`}
              data-testid="daily-rec-dismiss"
              title={bg ? "Скрий завинаги" : "Hide permanently"}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {expanded && (
          <>
            <div
              className={`text-xs sm:text-sm leading-relaxed whitespace-pre-wrap ${isDark ? "text-gray-200" : "text-gray-800"}`}
              data-testid="daily-rec-body"
            >
              {rec.recommendations}
            </div>

            {/* Quick action: apply recommendation → open Meal Intelligence */}
            <button
              onClick={() => {
                window.dispatchEvent(new Event("mi:refresh-suggestions"));
                setTimeout(() => {
                  const el = document.getElementById("meal-intelligence-anchor")
                    || document.querySelector('[data-testid="meal-intelligence-card"]');
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 200);
              }}
              className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium px-3 py-1.5 rounded-lg transition-all
                ${isDark ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 border border-emerald-500/30"
                         : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"}`}
              data-testid="daily-rec-open-mi"
              title={bg ? "Отвори Умно хранене, за да избереш от свежите предложения" : "Open Meal Intelligence"}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {bg ? "Отвори Умно хранене" : "Open Meal Intelligence"}
            </button>

            <div className={`mt-3 pt-2 border-t flex flex-wrap items-center justify-between gap-2 text-[10px] ${isDark ? "border-emerald-800/40 text-gray-500" : "border-emerald-200 text-gray-500"}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span>{bg ? "Базирано на:" : "Based on:"}</span>
                {ctx.phenotype && ctx.phenotype !== "?" && (
                  <span className={`px-1.5 py-0.5 rounded ${isDark ? "bg-white/5" : "bg-white"}`}>{ctx.phenotype}</span>
                )}
                {ctx.dominant_pattern && (
                  <span className={`px-1.5 py-0.5 rounded ${isDark ? "bg-white/5" : "bg-white"}`}>{ctx.dominant_pattern}</span>
                )}
                {ctx.flagged_labs?.length > 0 && (
                  <span className={`px-1.5 py-0.5 rounded ${isDark ? "bg-white/5" : "bg-white"}`}>
                    {ctx.flagged_labs.length} {bg ? "лаб." : "labs"}
                  </span>
                )}
                {ctx.mi_snapshot_count > 0 && (
                  <span className={`px-1.5 py-0.5 rounded ${isDark ? "bg-white/5" : "bg-white"}`}>
                    {ctx.mi_snapshot_count} {bg ? "храни" : "foods"}
                  </span>
                )}
              </div>
              <button
                onClick={handleOptOut}
                className={`inline-flex items-center gap-1 transition-colors ${isDark ? "text-gray-500 hover:text-gray-300" : "text-gray-400 hover:text-gray-600"}`}
                data-testid="daily-rec-optout"
              >
                <BellOff className="w-3 h-3" />
                {bg ? "Изключи ежедн. препоръки" : "Turn off daily recs"}
              </button>
            </div>

            {/* Push notification toggle — browser subscription (separate from backend opt-out) */}
            {push.supported && (
              <div className={`mt-2 pt-2 border-t flex items-center justify-between gap-2 text-[10px] ${isDark ? "border-emerald-800/40" : "border-emerald-200"}`} data-testid="daily-rec-push-toggle">
                <div className={`flex items-center gap-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  <Bell className="w-3 h-3" />
                  <span>{bg ? "Браузърни push известия" : "Browser push notifications"}</span>
                  {push.subscribed && (
                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${isDark ? "bg-emerald-900/40 text-emerald-300" : "bg-emerald-100 text-emerald-700"}`}>
                      {bg ? "активни" : "active"}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {push.subscribed ? (
                    <>
                      <button onClick={() => push.sendTest()}
                        className={`px-2 py-0.5 rounded transition-colors ${isDark ? "bg-white/5 hover:bg-white/10 text-gray-300" : "bg-white hover:bg-gray-50 text-gray-700"}`}
                        data-testid="daily-rec-push-test"
                        disabled={push.loading}
                      >
                        {bg ? "Тест" : "Test"}
                      </button>
                      <button onClick={() => push.disable()}
                        className={`px-2 py-0.5 rounded transition-colors ${isDark ? "bg-red-900/30 hover:bg-red-900/50 text-red-300" : "bg-red-50 hover:bg-red-100 text-red-700"}`}
                        data-testid="daily-rec-push-disable"
                        disabled={push.loading}
                      >
                        {bg ? "Изкл." : "Off"}
                      </button>
                    </>
                  ) : (
                    <button onClick={() => push.enable()}
                      className="px-2 py-0.5 rounded bg-gradient-to-r from-emerald-500 to-sky-500 text-white font-semibold shadow-sm hover:shadow"
                      data-testid="daily-rec-push-enable"
                      disabled={push.loading}
                    >
                      {push.loading ? (bg ? "Активиране…" : "Enabling…") : (bg ? "Активирай" : "Enable")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <AIModuleNotice variant="educational" />
    </div>
  );
}
